import { Suspense } from "react";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { CssBaseline, ThemeProvider, CircularProgress, Box } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { SessionProvider } from "./contexts/SessionContext";
import { InactivityProvider } from "./contexts/InactivityContext";
import { LiveAnnouncerProvider } from "./contexts/LiveAnnouncerContext";
import { SessionWarningModal } from "./components/common/SessionWarningModal";
import { InactivityWarningModal } from "./components/common/InactivityWarningModal";
import { AppErrorBoundary } from "./components/common/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RoleGate } from "./components/RoleGate";
import { Layout } from "./components/layout";
import aosTheme from "./theme/aosTheme";
import { UserRole } from "./api/generated.schemas";

// Lazy load all pages with retry logic for stale chunk handling
const LoginPage = lazyWithRetry(
  () => import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })),
  "LoginPage"
);
const DashboardPage = lazyWithRetry(() => import("./pages/DashboardPage"), "DashboardPage");
const PathwaysPage = lazyWithRetry(() => import("./pages/PathwaysPage"), "PathwaysPage");
const AIChatPage = lazyWithRetry(() => import("./pages/AIChatPage"), "AIChatPage");
const ReportsPage = lazyWithRetry(() => import("./pages/ReportsPage"), "ReportsPage");
const ProfilePage = lazyWithRetry(() => import("./pages/ProfilePage"), "ProfilePage");
const IntegrationsPage = lazyWithRetry(
  () => import("./pages/IntegrationsPage"),
  "IntegrationsPage"
);
const ResourcesPage = lazyWithRetry(() => import("./pages/ResourcesPage"), "ResourcesPage");
const CommunityPage = lazyWithRetry(() => import("./pages/CommunityPage"), "CommunityPage");
const KnowledgebasePage = lazyWithRetry(
  () => import("./pages/KnowledgebasePage"),
  "KnowledgebasePage"
);
const AdminSettingsPage = lazyWithRetry(
  () => import("./pages/AdminSettingsPage"),
  "AdminSettingsPage"
);
const TranscriptsPage = lazyWithRetry(() => import("./pages/TranscriptsPage"), "TranscriptsPage");
const TranscriptDetailPage = lazyWithRetry(
  () => import("./pages/TranscriptDetailPage"),
  "TranscriptDetailPage"
);
const AvatarPage = lazyWithRetry(() => import("./pages/AvatarPage"), "AvatarPage");
const CourseCatalogPage = lazyWithRetry(
  () => import("./pages/CourseCatalogPage"),
  "CourseCatalogPage"
);
const TeamTrainingPage = lazyWithRetry(
  () => import("./pages/TeamTrainingPage"),
  "TeamTrainingPage"
);
const TrainingRequirementsPage = lazyWithRetry(
  () => import("./pages/TrainingRequirementsPage"),
  "TrainingRequirementsPage"
);
const TeamManagementPage = lazyWithRetry(
  () => import("./pages/TeamManagementPage"),
  "TeamManagementPage"
);
const CoachingRolesAdminPage = lazyWithRetry(
  () => import("./pages/CoachingRolesAdminPage"),
  "CoachingRolesAdminPage"
);
const ResourceCategoriesAdminPage = lazyWithRetry(
  () => import("./pages/ResourceCategoriesAdminPage"),
  "ResourceCategoriesAdminPage"
);
const AgentToolsAdminPage = lazyWithRetry(
  () => import("./pages/AgentToolsAdminPage"),
  "AgentToolsAdminPage"
);
const AgentToolsAnalyticsPage = lazyWithRetry(
  () => import("./pages/AgentToolsAnalyticsPage"),
  "AgentToolsAnalyticsPage"
);
const ThinkificCallbackPage = lazyWithRetry(
  () => import("./pages/ThinkificCallbackPage").then((m) => ({ default: m.ThinkificCallbackPage })),
  "ThinkificCallbackPage"
);
const GoalsPage = lazyWithRetry(() => import("./pages/GoalsPage"), "GoalsPage");
const GoalDetailPage = lazyWithRetry(() => import("./pages/GoalDetailPage"), "GoalDetailPage");
const TeamGoalsPage = lazyWithRetry(() => import("./pages/TeamGoalsPage"), "TeamGoalsPage");
const PendingApprovalsPage = lazyWithRetry(
  () => import("./pages/PendingApprovalsPage"),
  "PendingApprovalsPage"
);
const SearchPage = lazyWithRetry(() => import("./pages/SearchPage"), "SearchPage");
const AboutPage = lazyWithRetry(() => import("./pages/AboutPage"), "AboutPage");
const PrivacyPolicyPage = lazyWithRetry(
  () => import("./pages/PrivacyPolicyPage"),
  "PrivacyPolicyPage"
);
const TermsOfServicePage = lazyWithRetry(
  () => import("./pages/TermsOfServicePage"),
  "TermsOfServicePage"
);
const UnauthorizedPage = lazyWithRetry(
  () => import("./pages/UnauthorizedPage"),
  "UnauthorizedPage"
);
const CallPerformancePage = lazyWithRetry(
  () => import("./pages/CallPerformancePage"),
  "CallPerformancePage"
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Cache optimization for NFR-1 (reduce redundant API calls)
      staleTime: 30 * 1000, // 30 seconds - data considered fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache
    },
  },
});

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "50vh",
      }}
    >
      <CircularProgress />
    </Box>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <ThemeProvider theme={aosTheme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <SessionProvider>
                <InactivityProvider>
                  <LiveAnnouncerProvider>
                    <SessionWarningModal />
                    <InactivityWarningModal />
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        {/* Public routes */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/unauthorized" element={<UnauthorizedPage />} />
                        <Route
                          path="/oauth/thinkific/callback"
                          element={<ThinkificCallbackPage />}
                        />

                        {/* Protected routes with Layout */}
                        <Route
                          element={
                            <ProtectedRoute>
                              <Layout />
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={<Navigate to="/dashboard" replace />} />
                          <Route path="dashboard" element={<DashboardPage />} />
                          <Route path="pathways" element={<PathwaysPage />} />
                          <Route path="training/catalog" element={<CourseCatalogPage />} />
                          <Route
                            path="training/team"
                            element={
                              <RoleGate
                                requiredRoles={[
                                  UserRole.manager,
                                  UserRole.director,
                                  UserRole.administrator,
                                ]}
                              >
                                <TeamTrainingPage />
                              </RoleGate>
                            }
                          />
                          <Route path="ai-coach" element={<AIChatPage />} />
                          <Route path="call-performance" element={<CallPerformancePage />} />
                          <Route path="profile" element={<ProfilePage />} />
                          <Route path="integrations" element={<IntegrationsPage />} />
                          <Route path="transcripts" element={<TranscriptsPage />} />
                          <Route path="transcripts/:id" element={<TranscriptDetailPage />} />
                          <Route path="resources" element={<ResourcesPage />} />
                          <Route path="community" element={<CommunityPage />} />
                          {/* Spec 071: /my-kb is the new knowledgebase home. */}
                          <Route path="my-kb" element={<KnowledgebasePage />} />
                          {/* Legacy /my-videos redirects to /my-kb for one release cycle. */}
                          <Route
                            path="my-videos"
                            element={<Navigate to="/my-kb" replace />}
                          />
                          <Route path="about" element={<AboutPage />} />
                          <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                          <Route path="terms-of-service" element={<TermsOfServicePage />} />
                          <Route path="avatar" element={<AvatarPage />} />
                          <Route path="goals" element={<GoalsPage />} />
                          <Route path="goals/:id" element={<GoalDetailPage />} />
                          <Route path="search" element={<SearchPage />} />
                          <Route
                            path="goals/team"
                            element={
                              <RoleGate
                                requiredRoles={[
                                  UserRole.manager,
                                  UserRole.director,
                                  UserRole.administrator,
                                ]}
                              >
                                <TeamGoalsPage />
                              </RoleGate>
                            }
                          />
                          <Route
                            path="goals/approvals"
                            element={
                              <RoleGate
                                requiredRoles={[
                                  UserRole.manager,
                                  UserRole.director,
                                  UserRole.administrator,
                                ]}
                              >
                                <PendingApprovalsPage />
                              </RoleGate>
                            }
                          />
                          <Route
                            path="reports"
                            element={
                              <RoleGate
                                requiredRoles={[
                                  UserRole.manager,
                                  UserRole.director,
                                  UserRole.administrator,
                                ]}
                              >
                                <ReportsPage />
                              </RoleGate>
                            }
                          />
                          <Route
                            path="admin/settings"
                            element={
                              <RoleGate requiredRoles={[UserRole.administrator]}>
                                <AdminSettingsPage />
                              </RoleGate>
                            }
                          />
                          <Route
                            path="admin/training-requirements"
                            element={
                              <RoleGate requiredRoles={[UserRole.administrator]}>
                                <TrainingRequirementsPage />
                              </RoleGate>
                            }
                          />
                          <Route
                            path="admin/team-management"
                            element={
                              <RoleGate requiredRoles={[UserRole.administrator]}>
                                <TeamManagementPage />
                              </RoleGate>
                            }
                          />
                          <Route
                            path="admin/coaching-roles"
                            element={
                              <RoleGate requiredRoles={[UserRole.administrator]}>
                                <CoachingRolesAdminPage />
                              </RoleGate>
                            }
                          />
                          <Route
                            path="admin/resource-categories"
                            element={
                              <RoleGate requiredRoles={[UserRole.administrator]}>
                                <ResourceCategoriesAdminPage />
                              </RoleGate>
                            }
                          />
                          <Route
                            path="admin/agent-tools"
                            element={
                              <RoleGate requiredRoles={[UserRole.super_admin]}>
                                <AgentToolsAdminPage />
                              </RoleGate>
                            }
                          />
                          <Route
                            path="admin/agent-tools/analytics"
                            element={
                              <RoleGate requiredRoles={[UserRole.super_admin]}>
                                <AgentToolsAnalyticsPage />
                              </RoleGate>
                            }
                          />
                        </Route>
                      </Routes>
                    </Suspense>
                  </LiveAnnouncerProvider>
                </InactivityProvider>
              </SessionProvider>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}

export default App;
